import { useState } from 'react'
import { clsx } from 'clsx'
import { CalendarClock, X } from 'lucide-react'
import {
  useCancelSchedule,
  useCreateSchedule,
  useGameweekSchedules,
  type CreateScheduleInput,
  type ScheduledJob,
  type ScheduleIntervalUnit,
  type ScheduleMode,
  type SurvivorScheduleAction,
} from '../queries'
import styles from './SurvivorScheduleCard.module.css'

// Only the three actions that belong to the survivor loop. Fetch stats and Process are
// fantasy-only — offering them here would let an admin schedule a job that either costs
// player-stat requests or fails outright on a provider with no player data.
const ACTIONS: Array<{ value: SurvivorScheduleAction; label: string; hint: string }> = [
  { value: 'LOCK',     label: 'Bloquear',          hint: 'Fecha os palpites e aplica os W.O.' },
  { value: 'RESULTS',  label: 'Buscar resultados', hint: '1 requisição — grava os placares' },
  { value: 'FINALIZE', label: 'Finalizar',         hint: 'Resolve palpites e elimina' },
]

const ACTION_LABEL: Record<string, string> = {
  LOCK:     'Bloquear',
  RESULTS:  'Buscar resultados',
  FINALIZE: 'Finalizar',
  // A schedule created from the fantasy panel can surface here; label it rather than
  // rendering a raw enum.
  FETCH:    'Fetch stats (fantasy)',
  PROCESS:  'Process (fantasy)',
  REOPEN:   'Reabrir janela (fantasy)',
}

const MODES: Array<{ value: ScheduleMode; label: string }> = [
  { value: 'ONCE',     label: 'Uma vez (data/hora)' },
  { value: 'INTERVAL', label: 'Intervalo (a cada N)' },
  { value: 'WINDOW',   label: 'Janela (a cada N, entre início e fim)' },
  { value: 'CRON',     label: 'Cron (avançado)' },
]

type SurvivorScheduleCardProps = {
  gameweekId:     string
  gameweekNumber: number
  /** Bounds the suggested polling window so the defaults land on the real round. */
  firstMatchAt:    string
  lastMatchEndsAt: string
}

export function SurvivorScheduleCard({
  gameweekId, gameweekNumber, firstMatchAt, lastMatchEndsAt,
}: SurvivorScheduleCardProps) {
  const { data: schedules, isPending, isError } = useGameweekSchedules(gameweekId)
  const cancel = useCancelSchedule(gameweekId)

  return (
    <div className={styles.card}>
      <header className={styles.header}>
        <CalendarClock size={15} className={styles.headerIcon} />
        <div>
          <h3 className={styles.title}>Agendamentos — Rodada {gameweekNumber}</h3>
          <p className={styles.hint}>
            Dispara uma ação da rodada por Hangfire no horário escolhido. Útil para deixar
            o <strong>Buscar resultados</strong> rodando durante os jogos e só finalizar
            quando tudo estiver encerrado.
          </p>
        </div>
      </header>

      {isPending && <div className={styles.empty}>Carregando agendamentos…</div>}
      {isError && <div className={styles.empty}>Erro ao carregar agendamentos.</div>}
      {!isPending && !isError && schedules?.length === 0 && (
        <div className={styles.empty}>Nenhum agendamento nesta rodada.</div>
      )}

      {schedules && schedules.length > 0 && (
        <div className={styles.list}>
          {schedules.map((s) => (
            <ScheduleRow
              key={s.id}
              schedule={s}
              onCancel={() => cancel.mutate(s.id)}
              isCancelling={cancel.isPending && cancel.variables === s.id}
            />
          ))}
        </div>
      )}

      <ScheduleForm
        gameweekId={gameweekId}
        firstMatchAt={firstMatchAt}
        lastMatchEndsAt={lastMatchEndsAt}
      />
    </div>
  )
}

function ScheduleRow({
  schedule, onCancel, isCancelling,
}: {
  schedule:     ScheduledJob
  onCancel:     () => void
  isCancelling: boolean
}) {
  return (
    <div className={styles.item}>
      <div className={styles.itemMain}>
        <div className={styles.itemTop}>
          <span className={styles.actionBadge}>
            {ACTION_LABEL[schedule.action] ?? schedule.action}
          </span>
          <span className={styles.cadence}>{formatCadence(schedule)}</span>
        </div>
        <span className={styles.meta}>
          {schedule.runCount} execuç{schedule.runCount === 1 ? 'ão' : 'ões'}
          {schedule.lastRunAt ? ` · última ${formatDateTime(schedule.lastRunAt)}` : ''}
        </span>
        {schedule.lastError && <p className={styles.error}>Erro: {schedule.lastError}</p>}
      </div>

      <div className={styles.itemSide}>
        <span className={clsx(styles.statusPill, styles[`status_${schedule.status}`])}>
          {schedule.status}
        </span>
        {schedule.status === 'SCHEDULED' && (
          <button
            type="button"
            className={styles.cancelBtn}
            onClick={onCancel}
            disabled={isCancelling}
          >
            <X size={11} /> {isCancelling ? 'Cancelando…' : 'Cancelar'}
          </button>
        )}
      </div>
    </div>
  )
}

function ScheduleForm({
  gameweekId, firstMatchAt, lastMatchEndsAt,
}: {
  gameweekId:      string
  firstMatchAt:    string
  lastMatchEndsAt: string
}) {
  // Default to the most useful survivor job: poll results across the round's match window.
  const [action,        setAction]        = useState<SurvivorScheduleAction>('RESULTS')
  const [mode,          setMode]          = useState<ScheduleMode>('WINDOW')
  const [runAt,         setRunAt]         = useState(() => toLocalInput(firstMatchAt, -60))
  const [intervalValue, setIntervalValue] = useState(15)
  const [intervalUnit,  setIntervalUnit]  = useState<ScheduleIntervalUnit>('MINUTES')
  const [windowStart,   setWindowStart]   = useState(() => toLocalInput(firstMatchAt, 0))
  // The stored end is an estimate (max kickoff + 2h); pad it so a late match still lands.
  const [windowEnd,     setWindowEnd]     = useState(() => toLocalInput(lastMatchEndsAt, 180))
  const [cron,          setCron]          = useState('')

  const create = useCreateSchedule(gameweekId)

  const toIso = (local: string) => new Date(local).toISOString()

  const isValid =
    mode === 'ONCE'     ? runAt !== '' :
    mode === 'INTERVAL' ? intervalValue >= 1 :
    mode === 'WINDOW'   ? intervalValue >= 1 && windowStart !== '' && windowEnd !== '' :
    /* CRON */            cron.trim() !== ''

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!isValid) return

    // Build only the keys this mode uses — never assign undefined.
    const input: CreateScheduleInput = { action, mode }
    if (mode === 'ONCE') input.runAt = toIso(runAt)
    if (mode === 'INTERVAL' || mode === 'WINDOW') {
      input.intervalValue = intervalValue
      input.intervalUnit  = intervalUnit
    }
    if (mode === 'WINDOW') {
      input.windowStart = toIso(windowStart)
      input.windowEnd   = toIso(windowEnd)
    }
    if (mode === 'CRON') input.cron = cron.trim()

    create.mutate(input)
  }

  const selected = ACTIONS.find((a) => a.value === action)

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <p className={styles.formTitle}>Novo agendamento</p>

      <div className={styles.row}>
        <label className={styles.field}>
          <span className={styles.label}>Ação</span>
          <select
            className={styles.select}
            value={action}
            onChange={(e) => setAction(e.target.value as SurvivorScheduleAction)}
            disabled={create.isPending}
          >
            {ACTIONS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
          </select>
          {selected && <span className={styles.fieldHint}>{selected.hint}</span>}
        </label>

        <label className={styles.field}>
          <span className={styles.label}>Modo</span>
          <select
            className={styles.select}
            value={mode}
            onChange={(e) => setMode(e.target.value as ScheduleMode)}
            disabled={create.isPending}
          >
            {MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </label>
      </div>

      {mode === 'ONCE' && (
        <div className={styles.row}>
          <label className={styles.field}>
            <span className={styles.label}>Data e hora</span>
            <input
              type="datetime-local"
              className={styles.input}
              value={runAt}
              onChange={(e) => setRunAt(e.target.value)}
              disabled={create.isPending}
            />
            <span className={styles.fieldHint}>
              Sugerido: 60 min antes do primeiro jogo (horário do bloqueio).
            </span>
          </label>
        </div>
      )}

      {(mode === 'INTERVAL' || mode === 'WINDOW') && (
        <div className={styles.row}>
          <label className={styles.field}>
            <span className={styles.label}>A cada</span>
            <input
              type="number"
              min={1}
              className={styles.input}
              value={intervalValue}
              onChange={(e) => setIntervalValue(Number(e.target.value))}
              disabled={create.isPending}
            />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>Unidade</span>
            <select
              className={styles.select}
              value={intervalUnit}
              onChange={(e) => setIntervalUnit(e.target.value as ScheduleIntervalUnit)}
              disabled={create.isPending}
            >
              <option value="MINUTES">Minutos (1–59)</option>
              <option value="HOURS">Horas (1–23)</option>
            </select>
            <span className={styles.fieldHint}>
              O provedor aceita 10 requisições por minuto — 15 min é folgado.
            </span>
          </label>
        </div>
      )}

      {mode === 'WINDOW' && (
        <div className={styles.row}>
          <label className={styles.field}>
            <span className={styles.label}>Início da janela</span>
            <input
              type="datetime-local"
              className={styles.input}
              value={windowStart}
              onChange={(e) => setWindowStart(e.target.value)}
              disabled={create.isPending}
            />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>Fim da janela</span>
            <input
              type="datetime-local"
              className={styles.input}
              value={windowEnd}
              onChange={(e) => setWindowEnd(e.target.value)}
              disabled={create.isPending}
            />
            <span className={styles.fieldHint}>
              Pré-preenchido com o fim estimado da rodada + 3 h.
            </span>
          </label>
        </div>
      )}

      {mode === 'CRON' && (
        <div className={styles.row}>
          <label className={clsx(styles.field, styles.fieldWide)}>
            <span className={styles.label}>Expressão cron</span>
            <input
              type="text"
              className={styles.input}
              value={cron}
              placeholder="*/15 * * * *"
              onChange={(e) => setCron(e.target.value)}
              disabled={create.isPending}
            />
          </label>
        </div>
      )}

      <button type="submit" className={styles.submitBtn} disabled={!isValid || create.isPending}>
        {create.isPending ? 'Agendando…' : 'Agendar'}
      </button>
    </form>
  )
}

// ISO → the local "YYYY-MM-DDTHH:mm" that datetime-local expects, offset by minutes.
function toLocalInput(iso: string, offsetMinutes: number): string {
  const d = new Date(new Date(iso).getTime() + offsetMinutes * 60_000)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
       + `T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function formatCadence(s: ScheduledJob): string {
  switch (s.mode) {
    case 'ONCE':
      return s.runAt ? `Uma vez · ${formatDateTime(s.runAt)}` : 'Uma vez'
    case 'INTERVAL':
      return `A cada ${formatInterval(s.intervalMinutes)}`
    case 'WINDOW':
      return `A cada ${formatInterval(s.intervalMinutes)} · janela `
           + `${formatDateTime(s.windowStart)}–${formatDateTime(s.windowEnd)}`
    case 'CRON':
      return `cron: ${s.cron ?? ''}`
  }
}

function formatInterval(minutes: number | null): string {
  if (minutes === null) return '—'
  return minutes % 60 === 0 ? `${minutes / 60} h` : `${minutes} min`
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  })
}
